import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';

import { AREA_COLORS, LineChart, OTHER_COLOR, PanelChart, Sparkline } from '@/components/charts';
import { AiOff, Card, CustomersToggle, Kpi, Loaded, PageHead, RangeSeg, StackBar } from '@/components/ui';
import { useApi } from '@/lib/api';
import { cx } from '@/lib/cx';
import { countDelta, fmtDay, fmtInt, fmtMoney, fmtPct, fmtSeconds, rateDelta } from '@/lib/format';
import { feedHref, RANGES, useCustomers, useParam } from '@/lib/params';
import type { FeatureArea, FeaturesList } from '@/lib/types';

const HIGH_NOT_OK = 0.08;

function CompareAll({ list }: { list: UseQueryResult<FeaturesList, Error> }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  return (
    <>
      <PageHead title="Compare all areas" subtitle="Asks, failures and cost per product area">
        <RangeSeg options={RANGES.standard} fallback="30d" />
        <CustomersToggle />
      </PageHead>
      <Loaded query={list}>
        {(data) => (
          <div className="card" style={{ padding: '6px 8px' }}>
            <table>
              <thead>
                <tr>
                  <th>Area</th>
                  <th className="num">Companies</th>
                  <th className="num">Asks</th>
                  <th>Trend</th>
                  <th className="num">Not-ok</th>
                  <th className="num">p95</th>
                  <th className="num">Cost</th>
                  <th className="num">$ / ask</th>
                </tr>
              </thead>
              <tbody>
                {data.areas.map((a) => (
                  <tr key={a.key} onClick={() => navigate(`/features/${a.key}${search}`)}>
                    <td>
                      <b>{a.label}</b>
                    </td>
                    <td className="num">{fmtInt(a.companies)}</td>
                    <td className="num">{fmtInt(a.asks)}</td>
                    <td>
                      <Sparkline values={a.spark} width={60} height={16} />
                    </td>
                    <td className="num">
                      {(a.not_ok_rate ?? 0) >= HIGH_NOT_OK ? (
                        <span className="pill bad">{fmtPct(a.not_ok_rate)}</span>
                      ) : (
                        fmtPct(a.not_ok_rate)
                      )}
                    </td>
                    <td className="num">{fmtSeconds(a.p95_s)}</td>
                    <td className="num">{fmtMoney(a.cost)}</td>
                    <td className="num">{fmtMoney(a.cost_per_ask)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Loaded>
    </>
  );
}

function Who({ who }: { who: Extract<FeatureArea['who'], { applies: true }> }) {
  const total = who.compass_ai + who.users + who.external_ai;
  const parts = [
    { key: 'ai', label: 'Compass AI', value: who.compass_ai, color: 'var(--s1)' },
    { key: 'users', label: 'Users by hand', value: who.users, color: 'var(--s2)' },
    { key: 'ext', label: 'External AI (MCP)', value: who.external_ai, color: 'var(--s3)' },
  ];
  if (!total) return <div className="muted">No {who.action} events in this range.</div>;
  return (
    <>
      <StackBar height={14} parts={parts.map((p) => ({ ...p, share: p.value / total }))} />
      <div style={{ marginTop: 10, display: 'grid', gap: 4 }} className="legend">
        {parts.map((p) => (
          <span key={p.key}>
            <i style={{ background: p.color, width: 8, height: 8 }} />
            {p.label} · {fmtPct(p.value / total)}
          </span>
        ))}
      </div>
      <div className="note">From {who.action} actors.</div>
    </>
  );
}

function AreaBody({ d, range, customers }: { d: FeatureArea; range: string; customers: boolean }) {
  const navigate = useNavigate();
  const k = d.kpis;
  const models = d.models;
  const split = models.split.map((m, i) => ({
    key: m.label,
    label: `${m.label} · ${fmtPct(m.share)}`,
    share: m.share ?? 0,
    color: i < AREA_COLORS.length ? AREA_COLORS[i] : OTHER_COLOR,
  }));
  const toFeed = (extra: Record<string, string>) =>
    navigate(feedHref({ area: d.area.key, range, customers, ...extra }));
  return (
    <>
      <div className="kpis">
        <Kpi
          label="Companies using"
          value={fmtInt(k.companies.value)}
          footer={<span className="d muted">{fmtPct(k.companies.share_of_active)} of active</span>}
        />
        <Kpi
          label="Asks"
          value={fmtInt(k.asks.value)}
          delta={countDelta(k.asks.value, k.asks.prev)}
          onClick={() => toFeed({})}
        />
        <Kpi
          label="Not-ok rate"
          value={fmtPct(k.not_ok_rate.value)}
          delta={rateDelta(k.not_ok_rate.value, k.not_ok_rate.prev)}
          onClick={() => toFeed({ outcome: 'not_ok' })}
        />
        <Kpi
          label="Time to answer"
          value={
            <span style={{ fontSize: 22 }}>
              {fmtSeconds(k.p50_s)}{' '}
              <span className="faint" style={{ fontSize: 14 }}>
                p95 {fmtSeconds(k.p95_s)}
              </span>
            </span>
          }
          footer={<span className="d muted">median</span>}
        />
        <Kpi
          label="AI cost"
          value={fmtMoney(k.cost.value)}
          footer={<span className="d muted">{k.cost.per_ask !== null && `${fmtMoney(k.cost.per_ask)} per ask`}</span>}
        />
      </div>
      <Card style={{ marginTop: 12 }} title="Asks and not-ok rate">
        <PanelChart
          labels={d.days.map(fmtDay)}
          bars={d.asks_per_day}
          line={d.not_ok_rate_per_day}
          title={(i) => fmtDay(d.days[i])}
        />
      </Card>
      <div className="g2">
        <Card
          title="Steps and tools"
          right={
            <span className="link" onClick={() => toFeed({})}>
              Open in Feed →
            </span>
          }
        >
          {d.steps.length === 0 ? (
            <div className="muted">No AI calls in this area and range.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Step</th>
                  <th className="num">Calls</th>
                  <th className="num">Fail</th>
                  <th className="num">p95</th>
                  <th className="num">Cost</th>
                  <th>14 days</th>
                </tr>
              </thead>
              <tbody>
                {d.steps.map((s) => {
                  const bad = (s.fail_rate ?? 0) >= 0.1;
                  return (
                    <tr key={s.name}>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.name}>
                        <b>{s.name}</b>
                      </td>
                      <td className="num">{fmtInt(s.calls)}</td>
                      <td className="num">
                        <span className={cx('pill', bad ? 'bad' : 'neu')}>{fmtPct(s.fail_rate)}</span>
                      </td>
                      <td className="num">{fmtSeconds(s.p95_s)}</td>
                      <td className="num">{fmtMoney(s.cost)}</td>
                      <td>
                        <Sparkline values={s.spark} width={60} height={16} bad={bad} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
        <Card title="Models">
          <div className="kv" style={{ gridTemplateColumns: '120px 1fr' }}>
            <span className="k">Calls by model</span>
            <span>
              {split.length ? (
                <>
                  <StackBar parts={split} />
                  <div className="legend" style={{ marginTop: 6 }}>
                    {split.map((s) => (
                      <span key={s.key}>
                        <i style={{ background: s.color, width: 8, height: 8 }} />
                        {s.label}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                '–'
              )}
            </span>
            <span className="k">Fallback rate</span>
            <span>
              {models.fallback_rate === null ? (
                <span className="faint">not logged yet</span>
              ) : (
                <>
                  <b>{fmtPct(models.fallback_rate)}</b> of calls fell back to another model
                </>
              )}
            </span>
            <span className="k">Retries</span>
            <span>
              {models.retry_rate === null ? (
                <span className="faint">not logged yet</span>
              ) : (
                `${fmtPct(models.retry_rate)} of calls needed a retry`
              )}
            </span>
            <span className="k">Prompt cache</span>
            <span>
              {models.cache_share === null ? '–' : `${fmtPct(models.cache_share)} of input tokens read from cache`}
            </span>
            <span className="k">Abandoned</span>
            <span>{fmtInt(models.abandoned)} streams closed before the end</span>
          </div>
        </Card>
      </div>
      <div className="g3">
        <Card title="Who does the work">
          {d.who.applies ? (
            <Who who={d.who} />
          ) : (
            <div className="muted">Only tracked for Forecasting, from forecast edits.</div>
          )}
        </Card>
        <Card title="Adoption">
          <LineChart
            labels={d.adoption.weeks}
            series={[{ key: 'companies', label: 'Companies', color: 'var(--brand-2)', values: d.adoption.companies }]}
            width={260}
            height={90}
            endLabels={false}
            format={fmtInt}
            title={(i) => `${d.adoption.weeks[i]}: ${d.adoption.companies[i]} companies used ${d.area.label}`}
          />
          <div className="note">
            <b style={{ color: 'var(--good)' }}>{d.adoption.started} started</b>
            {d.adoption.started_names.length > 0 && ` (${d.adoption.started_names.join(', ')})`} ·{' '}
            <b style={{ color: 'var(--bad)' }}>{d.adoption.stopped} stopped</b>
            {d.adoption.stopped_names.length > 0 && `: ${d.adoption.stopped_names.join(', ')}`}
          </div>
        </Card>
        <Card title="Top intents here">
          <AiOff what="Top intents" />
        </Card>
      </div>
    </>
  );
}

function AreaView({ area }: { area: string }) {
  const [range] = useParam('range', '30d');
  const [customers] = useCustomers();
  const query = useApi<FeatureArea>(`/features/${area}`, { range, customers });
  return (
    <>
      <PageHead title={query.data?.area.label ?? ' '} subtitle={query.data?.area.description}>
        <RangeSeg options={RANGES.standard} fallback="30d" />
        <CustomersToggle />
      </PageHead>
      <Loaded query={query}>{(d) => <AreaBody d={d} range={range} customers={customers} />}</Loaded>
    </>
  );
}

export function FeaturesPage() {
  const { area } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [range] = useParam('range', '30d');
  const [customers] = useCustomers();
  const list = useApi<FeaturesList>('/features', { range, customers });
  return (
    <div className="side">
      <aside className="rail">
        <div className="sec">Feature areas</div>
        <div className={cx('area', !area && 'on')} onClick={() => navigate(`/features${search}`)}>
          Compare all
          {list.data && <span className="m">{list.data.areas.length} areas</span>}
        </div>
        {list.data?.areas.map((a) => (
          <div
            key={a.key}
            className={cx('area', a.key === area && 'on')}
            onClick={() => navigate(`/features/${a.key}${search}`)}
          >
            <span>{a.label}</span>
            <span className="m">
              {fmtInt(a.asks)} ·{' '}
              <span style={(a.not_ok_rate ?? 0) >= HIGH_NOT_OK ? { color: 'var(--bad)' } : undefined}>
                {fmtPct(a.not_ok_rate)}
              </span>
            </span>
          </div>
        ))}
        <div className="note">Asks · not-ok rate, last {range}</div>
      </aside>
      <div className="page">{area ? <AreaView key={area} area={area} /> : <CompareAll list={list} />}</div>
    </div>
  );
}
